/**
 * Fachliche Konstanten und Aufzählungen, die von API, Web und Domänenlogik
 * gemeinsam genutzt werden. Bewusst framework-frei.
 */

// ── Mandant / Auth ───────────────────────────────────────────
export const MEMBERSHIP_ROLES = ["owner", "admin", "office", "field"] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

// ── Steuer (Österreich) ──────────────────────────────────────
export const TAX_SCHEMES = ["regel", "kleinunternehmer"] as const;
export type TaxScheme = (typeof TAX_SCHEMES)[number];

/** Zulässige USt-Sätze in Österreich (Prozent). 0 = steuerfrei / Reverse Charge. */
export const AT_TAX_RATES = [20, 13, 10, 0] as const;
export type AtTaxRate = (typeof AT_TAX_RATES)[number];

export const DEFAULT_TAX_RATE: AtTaxRate = 20;

/** Vermerk für Kleinunternehmer-Rechnungen. */
export const KLEINUNTERNEHMER_NOTE =
  "Gemäß § 6 Abs. 1 Z 27 UStG wird keine Umsatzsteuer berechnet.";

/**
 * Pflichtvermerk bei Übergang der Steuerschuld auf Bauleistungen (§ 19 Abs. 1a UStG).
 * Wird pro Mandant über den Standardtext `invoice_reverse_charge` überschreibbar.
 */
export const REVERSE_CHARGE_BAU_NOTE =
  "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge) gemäß § 19 Abs. 1a UStG. Die Umsatzsteuer ist vom Rechnungsempfänger abzuführen.";

/** Kleinbetragsrechnungs-Grenze (§ 11 Abs. 6 UStG) in Cent. */
export const KLEINBETRAG_LIMIT_CENTS = 40000;

/** Kleinunternehmer-Umsatzgrenze (§ 6 Abs. 1 Z 27 UStG, Stand 2025) in Cent — Nettoumsatz p.a. */
export const KLEINUNTERNEHMER_LIMIT_CENTS = 4200000;

// ── Nummernkreise ────────────────────────────────────────────
export const DOC_TYPES = [
  "quote",
  "invoice",
  "credit_note",
  "contract",
  "customer",
  "order",
] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const DEFAULT_NUMBER_FORMATS: Record<DocType, string> = {
  quote: "AN{YY}{SEQ}",
  invoice: "RE{YY}{SEQ}",
  credit_note: "GS-{YYYY}-{SEQ:4}",
  contract: "WV-{YYYY}-{SEQ:3}",
  // Kundennummer: fortlaufend, NICHT jahresbezogen (ein Kunde behält seine Nummer dauerhaft)
  customer: "{SEQ:4}",
  order: "AUF{YY}{SEQ}",
};

// ── Aufträge ─────────────────────────────────────────────────
/**
 * Auftragsstatus — folgt dem Angebot, ohne dass man einen Auftrag je von Hand anlegen muss:
 * quoted (Angebot versendet) → ordered (Angebot angenommen) → done (Auftrag erledigt, von Hand).
 * cancelled = Angebot abgelehnt bzw. Auftrag storniert.
 * (Früher: open | in_progress — per Migration auf "ordered" umgestellt.)
 */
export const ORDER_STATUSES = [
  "quoted",
  "ordered",
  "done",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  quoted: "Angeboten",
  ordered: "Beauftragt",
  done: "Erledigt",
  cancelled: "Abgelehnt / Storniert",
};

/** Periode für nicht jahresbezogene Nummernkreise (Kundennummer). */
export const NON_PERIODIC = "-";

// ── Verträge & Objekte (M5) ──────────────────────────────────
export const PROPERTY_SYSTEM_TYPES = [
  "heizung",
  "sanitaer",
  "klima",
  "lueftung",
  "badumbau",
  "rohrbruch",
  "sonstige",
] as const;
export type PropertySystemType = (typeof PROPERTY_SYSTEM_TYPES)[number];

export const PROPERTY_SYSTEM_TYPE_LABELS: Record<PropertySystemType, string> = {
  heizung: "Heizung",
  sanitaer: "Sanitär",
  klima: "Klima",
  lueftung: "Lüftung",
  badumbau: "Badumbau",
  rohrbruch: "Rohrbruch",
  sonstige: "Sonstige",
};

export const CONTRACT_TYPES = ["wartung", "service", "sonstige"] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];

export const CONTRACT_STATUSES = [
  "draft",
  "active",
  "paused",
  "ended",
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Intervall → Monate */
export const CONTRACT_INTERVALS: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  yearly: 12,
};

export const CONTRACT_AUTO_ACTIONS = [
  "none",
  "create_quote",
  "create_invoice",
  "create_task",
] as const;
export type ContractAutoAction = (typeof CONTRACT_AUTO_ACTIONS)[number];

export const TASK_STATUSES = ["open", "done"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

// ── Umsatzsteuer / UVA (M6) ──────────────────────────────────
export const UST_BESTEUERUNGSART = ["soll", "ist"] as const;
export type UstBesteuerungsart = (typeof UST_BESTEUERUNGSART)[number];

/**
 * Kennzahlen-Zuordnung nach österr. U30-Formular (Standardbelegung).
 * Vor produktiver Nutzung mit dem Steuerberater bestätigen.
 */
export const UVA_KENNZAHLEN = {
  gesamt_bemessung: {
    kz: "000",
    label: "Gesamtbetrag der Bemessungsgrundlagen",
  },
  umsatz_20: { kz: "022", label: "davon 20 % Normalsteuersatz (Bemessung)" },
  umsatz_13: { kz: "006", label: "davon 13 % (Bemessung)" },
  umsatz_10: { kz: "029", label: "davon 10 % (Bemessung)" },
  umsatz_0: { kz: "016", label: "Steuerfreie Umsätze (ohne Vorsteuerabzug)" },
  ust_20: { kz: "USt20", label: "USt aus 20 %-Umsätzen" },
  ust_13: { kz: "USt13", label: "USt aus 13 %-Umsätzen" },
  ust_10: { kz: "USt10", label: "USt aus 10 %-Umsätzen" },
  rc_bau_umsatz: {
    kz: "021",
    label: "Bauleistungen § 19 Abs. 1a (Steuerschuld Empfänger)",
  },
  rc_ust: { kz: "057", label: "Reverse Charge: geschuldete USt (§ 19)" },
  vorsteuer: { kz: "060", label: "Abziehbare Vorsteuer" },
  rc_vorsteuer: { kz: "061", label: "Reverse Charge: Vorsteuer-Gegenbuchung" },
  zahllast: { kz: "095", label: "Zahllast (+) / Gutschrift (−)" },
} as const;

// ── Belege / Buchungs-Export (M6) ────────────────────────────
export const RECEIPT_CATEGORIES = [
  "Material/Wareneinkauf",
  "Fremdleistung",
  "Werkzeug/Geräte",
  "Fahrzeug/Treibstoff",
  "Miete/Betriebskosten",
  "Versicherung",
  "Büro/Software",
  "Fortbildung",
  "Sonstiges",
] as const;

export const EXPORT_FORMATS = [
  { id: "generic_csv", label: "Generisch (CSV)", ext: "csv" },
  { id: "datev_csv", label: "DATEV (CSV) — geplant", ext: "csv" },
  { id: "bmd_csv", label: "BMD (CSV) — geplant", ext: "csv" },
] as const;

// ── Angebots-/Rechnungspositionen ────────────────────────────
/** discount = Rabatt-Zeile auf einen Positionsbereich (Prozent). */
export const LINE_ITEM_KINDS = [
  "group",
  "position",
  "text",
  "discount",
  "subtotal",
] as const;
export type LineItemKind = (typeof LINE_ITEM_KINDS)[number];

/** mandatory = fixe Leistung, optional = Optionspreis (Eventualposition), alternative = entweder/oder */
export const LINE_ITEM_MODES = [
  "mandatory",
  "optional",
  "alternative",
] as const;
export type LineItemMode = (typeof LINE_ITEM_MODES)[number];

/** material = Ware, labor = Arbeitszeit/Leistung, flat = Pauschale */
export const ITEM_CATEGORIES = ["material", "labor", "flat"] as const;
export type ItemCategory = (typeof ITEM_CATEGORIES)[number];

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  material: "Material",
  labor: "Leistung",
  flat: "Pauschale",
};

/** Vorschläge für die frei wählbare Mengeneinheit. */
export const UNIT_PRESETS = [
  "Stk.",
  "ST",
  "PA",
  "STD",
  "GAR",
  "SET",
  "m",
  "lfm",
  "m²",
  "kg",
  "Pauschale",
] as const;

// ── Dokument-Status ──────────────────────────────────────────
export const QUOTE_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "rejected",
  "expired",
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const INVOICE_STATUSES = [
  "draft",
  "sent",
  "partially_paid",
  "paid",
  "overdue",
  "cancelled",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_KINDS = [
  "standalone",
  "advance",
  "partial",
  "final",
  "credit_note",
] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

export const PAYMENT_METHODS = ["transfer", "card", "cash"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

// ── Standardtexte ────────────────────────────────────────────
export const STANDARD_TEXT_SCOPES = [
  "quote_intro",
  "quote_closing",
  "quote_payment_terms",
  "quote_validity",
  "invoice_intro",
  "invoice_closing",
  "invoice_reverse_charge",
  "doc_legal_note",
  "doc_court_venue",
  "email_quote",
  "email_invoice",
] as const;
export type StandardTextScope = (typeof STANDARD_TEXT_SCOPES)[number];

export const STANDARD_TEXT_SCOPE_LABELS: Record<string, string> = {
  quote_intro: "Angebot — Einleitung",
  quote_closing: "Angebot — Schlusstext",
  quote_payment_terms: "Angebot — Zahlungsbedingungen",
  quote_validity: "Angebot — Gültigkeitsdauer",
  invoice_intro: "Rechnung — Einleitung",
  invoice_closing: "Rechnung — Schlusstext",
  invoice_reverse_charge:
    "Rechnung — Reverse-Charge-Hinweis (§ 19 Abs. 1a UStG)",
  doc_legal_note: "AGB-/Gerichtsstandshinweis (Angebot & Rechnung)",
  doc_court_venue: "Gerichtsstand (Angebot & Rechnung)",
  email_quote: "E-Mail-Text Angebot",
  email_invoice: "E-Mail-Text Rechnung",
};

// ── Betrieb / Personen ───────────────────────────────────────
/** Hauptnutzer des Betriebs — Vorbelegung für das Feld „SachbearbeiterIn". */
export const DEFAULT_HANDLER_NAME = "Ratko Mirkovic";

// ── Kunden ───────────────────────────────────────────────────
export const CUSTOMER_KINDS = ["person", "company"] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];

/** nur bei kind=person relevant */
export const SALUTATIONS = ["herr", "frau"] as const;
export type Salutation = (typeof SALUTATIONS)[number];
export const SALUTATION_LABELS: Record<Salutation, string> = {
  herr: "Herr",
  frau: "Frau",
};

/** billing = Rechnungsadresse, site = Objekt-/Baustellenadresse, postal = abweichende Postanschrift */
export const ADDRESS_KINDS = ["billing", "site", "postal"] as const;
export type AddressKind = (typeof ADDRESS_KINDS)[number];

export const JOURNAL_ENTRY_TYPES = [
  "note",
  "call",
  "email",
  "meeting",
  "site_visit",
  "system",
] as const;
export type JournalEntryType = (typeof JOURNAL_ENTRY_TYPES)[number];

// ── Großhändler-Konnektoren ──────────────────────────────────
export const SUPPLIER_CONNECTOR_KINDS = [
  "none",
  "datanorm",
  "ids_connect",
  "oci",
  "ugl",
  "open_masterdata",
] as const;
export type SupplierConnectorKind = (typeof SUPPLIER_CONNECTOR_KINDS)[number];

/** Aktuell unterstützt und wählbar: Katalog per Datanorm-Datei und IDS Connect (Absprung in den Händlershop).
 * OCI, UGL und Open Masterdata sind nicht mehr wählbar; bestehende Altwerte in der Datenbank bleiben lesbar. */
export const ACTIVE_SUPPLIER_CONNECTOR_KINDS = ["datanorm", "ids_connect"] as const;

export const CURRENCY = "EUR";

// ── Ausgaben (Modul, per Feature-Schalter) ───────────────────
export const EXPENSE_CATEGORIES = [
  'material',
  'fahrzeug',
  'werkzeug',
  'personal',
  'miete',
  'versicherung',
  'marketing',
  'buero',
  'sonstiges',
] as const
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number]
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  material: 'Material / Ware',
  fahrzeug: 'Fahrzeug / Kfz',
  werkzeug: 'Werkzeug / Geräte',
  personal: 'Personal / Löhne',
  miete: 'Miete / Betriebskosten',
  versicherung: 'Versicherung / Gebühren',
  marketing: 'Werbung / Marketing',
  buero: 'Büro / Software / Telefon',
  sonstiges: 'Sonstiges',
}
